import React, { useEffect, useMemo, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { failure } from '~/redux/actions/snackbar-actions';
import { getTeamMembersData } from '~/redux/actions/user-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import OlcButton from '~/components/General/OlcButton';
import OlcDropdownSearch from '~/components/General/OlcDropdownSearch';
import OlcFormModal from '~/components/General/OlcFormModal';

// Mui components
import { Box, Typography } from '@mui/material';

// Styles
import './styles.scss';

const FilterModal = ({
  handleClose,
  open,
  updatedProducts,
  setSelectedTemplateIds,
  handleApplyFilter,
  handleClearFilter,
}) => {
  const dispatch = useDispatch();
  const [selectedFields, setSelectedFields] = useState([]);

  const searchProductIds = useSelector((state) => state.templateReducer.searchProductIds);
  const searchCreator = useSelector((state) => state.templateReducer.searchCreator);
  const teamMembers = useSelector((state) => state.userReducers.teamMembers.rows) || [];

  const creatorOptions = useMemo(
    () =>
      teamMembers
        .map((user) => ({
          id: user.id,
          title: [user.firstName, user.lastName].filter(Boolean).join(' ').trim(),
        }))
        .filter((user) => user.title),
    [teamMembers]
  );

  const [selectedCreator, setSelectedCreator] = useState(null);

  const handleSearch = () => {
    const productsIds = updatedProducts
      .filter((product) => selectedFields.includes(product.title))
      .map((product) => product.title)
      .join(',');

    const selectedIds = JSON.stringify(
      updatedProducts
        .filter((product) => selectedFields.includes(product.title))
        .map((product) => Number(product.id))
    );

    setSelectedTemplateIds(selectedFields.length > 0 ? selectedIds : null);

    const creator = selectedCreator?.title || '';

    if (!creator && !productsIds) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }
    handleApplyFilter({
      productsIds,
      selectedIds: selectedFields.length > 0 ? selectedIds : null,
      createdText: creator,
    });
    handleClose();
  };

  const handleClear = () => {
    setSelectedTemplateIds(null);
    setSelectedCreator(null);
    handleClearFilter();
    handleClose();
  };

  useEffect(() => {
    if (!searchProductIds) {
      setSelectedFields([]);
      return;
    }

    const productTitles = searchProductIds.split(',');
    const selectedProducts = updatedProducts
      .filter((product) => productTitles.includes(product.title))
      .map((product) => product.title);

    setSelectedFields(selectedProducts);
  }, [searchProductIds, updatedProducts]);

  useEffect(() => {
    if (open) {
      dispatch(getTeamMembersData({ page: 1, pageSize: 100 }));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (!searchCreator) {
      setSelectedCreator(null);
      return;
    }

    const matchingCreator = creatorOptions.find((creator) => creator.title === searchCreator);
    setSelectedCreator(
      matchingCreator || {
        id: `saved-${searchCreator}`,
        title: searchCreator,
      }
    );
  }, [creatorOptions, searchCreator]);

  const footer = (
    <>
      <OlcButton variant="secondary" onClick={handleClear}>
        Clear
      </OlcButton>
      <OlcButton
        variant="primary"
        onClick={handleSearch}
        disabled={!selectedCreator && !selectedFields.length}
      >
        Apply
      </OlcButton>
    </>
  );

  return (
    <OlcFormModal
      open={open}
      onClose={handleClose}
      title="Filters"
      className="filterModalWrapper"
      contentClassName="templateFilterDialogContent"
      footer={footer}
    >
      <Box className="filterModalContent">
        <Box className="inputWrapper">
          <Typography>Product Type</Typography>
          <MultiSelect
            options={updatedProducts}
            selectedValue={selectedFields}
            setSelectedValue={setSelectedFields}
            productType={true}
            placeHolderText="Select Product Type"
            noOptionText="No Product Type to show"
            clearable={true}
          />
        </Box>
        <Box className="inputWrapper">
          <Typography>Created by</Typography>
          <OlcDropdownSearch
            placeHolderText="Created by"
            searchFieldPlaceholderText="creators"
            noOptionText="No creators to show"
            options={creatorOptions}
            selectedValue={selectedCreator}
            setSelectedValue={setSelectedCreator}
            clearable
          />
        </Box>
      </Box>
    </OlcFormModal>
  );
};

export default FilterModal;
